import { cloneDeep, isEmpty as _isEmpty, isEqual } from 'lodash-es'

const SAVABLE_SETTING = [
  'ownership',
  'sortOrder',
  'sortId',
  'pageSize',
  'visibleColumnIds',
  'search',
  'searchVisibleOnly',
  'filters',
]

function getDefaultState() {
  return {
    appName: '',
    modelName: '',
    modelType: '',
    vuexUid: '',
    ownership: null,
    sortOrder: null,
    sortId: null,
    pageSize: null,
    search: null,
    searchVisibleOnly: null,
    visibleColumnIds: null,
    filters: null,
    savedFilters: {},
    context: getDefaultContext(),
  }
}

function getDefaultContext() {
  return {
    appName: '',
    modelName: '',
    modelType: '',
    key: '',
    id: '',
  }
}

const actions = {
  async initModule({ dispatch }, payload) {
    await dispatch('initMetaKeys', payload)
  },
  initMetaKeys({ state }, payload) {
    const { appName, modelName, modelType, vuexUid } = payload
    Object.assign(state, { appName, modelName, modelType, vuexUid })
  },
  initSessionSettings({ state, commit, getters }, defaultSettings) {
    const {
      defaultVisibleColumnIds,
      defaultFilters,
      initFilters,
      initOwnership,
    } = defaultSettings

    let visibleColumnIds
    if (state.visibleColumnIds !== null) {
      visibleColumnIds = state.visibleColumnIds
    } else if (
      getters.serverSettings.visibleColumnIds &&
      getters.serverSettings.visibleColumnIds.length
    ) {
      visibleColumnIds = getters.serverSettings.visibleColumnIds
    } else {
      visibleColumnIds = defaultVisibleColumnIds
    }
    let filters = {}
    let filterKeys =
      (typeof initFilters !== 'undefined' && Object.keys(initFilters)) || []
    if (filterKeys.length > 0) {
      filters = {
        ...defaultFilters,
        ...initFilters,
      }
      // Move the column with an 'init' filter first in display
      visibleColumnIds = [...filterKeys, ...visibleColumnIds]
    } else {
      const hasStoredServerFilters = Object.keys(
        getters.serverSettings.filters || {},
      ).length
      const hasSessionFilters = state.filters !== null
      const initialDefaultFilters =
        !hasStoredServerFilters && !hasSessionFilters ? defaultFilters : {}
      filters = {
        ...initialDefaultFilters,
        ...getters.serverSettings.filters,
        ...state.filters,
      }
    }
    let ownership = state.ownership?.name ? state.ownership : initOwnership

    commit('INIT_SETTINGS', {
      ...getters.defaultSettings,
      ...getters.serverSettings,
      ...(ownership && { ownership }),
      ...(state.sortOrder !== null && { sortOrder: state.sortOrder }),
      ...(state.sortId !== null && { sortId: state.sortId }),
      ...(state.pageSize !== null && { pageSize: state.pageSize }),
      ...(state.search !== null && { search: state.search }),
      ...(state.searchVisibleOnly !== null && {
        searchVisibleOnly: state.searchVisibleOnly,
      }),
      filters: filters,
    })
    commit('SET_VISIBLE_COLUMNS_IDS', visibleColumnIds)
  },
  resetToDefaultSettings({ commit, getters }, defaultSettings) {
    const { defaultVisibleColumnIds, defaultFilters } = defaultSettings
    commit('INIT_SETTINGS', {
      ...getters.defaultSettings,
      ...{ visibleColumnIds: defaultVisibleColumnIds },
      ...{ filters: defaultFilters },
    })
  },
  resetToServerSettings({ commit, getters }, defaultSettings) {
    const { defaultVisibleColumnIds, defaultFilters } = defaultSettings
    commit('INIT_SETTINGS', {
      ...getters.defaultSettings,
      ...{ visibleColumnIds: defaultVisibleColumnIds },
      ...{ filters: defaultFilters },
      ...getters.serverSettings,
    })
  },
  toggleColumns({ state, commit }, { ids, show }) {
    let newVisible = show
      ? [...state.visibleColumnIds, ...ids]
      : state.visibleColumnIds.filter((cId) => !ids.includes(cId))
    if (newVisible.length === 0) newVisible = ['id', 'name']
    commit('SET_VISIBLE_COLUMNS_IDS', newVisible)
  },
  toggleColumnById({ state, dispatch }, { id, index }) {
    const isVisible = state.visibleColumnIds.includes(id)
    if (isVisible) {
      dispatch('removeColumnById', { id })
    } else {
      dispatch('showColumn', { id, index })
    }
  },
  showColumn({ state, commit }, { id, index }) {
    const lastIndex = state.visibleColumnIds.length
    const addToIndex =
      index !== undefined && index > -1 && index <= lastIndex
        ? index
        : lastIndex
    commit('INSERT_COLUMN', { id, index: addToIndex })
  },
  showColumns({ dispatch }, { ids, index }) {
    ids.reverse()
    ids.forEach((id) => {
      dispatch('showColumn', { id, index: index })
    })
  },
  removeColumnById({ state, dispatch }, { id }) {
    const index = state.visibleColumnIds.indexOf(id)
    dispatch('removeColumnByIndex', { index })
  },
  removeColumnsByIndices({ dispatch }, { indices }) {
    // sort reverse to remove last index first
    // avoids shifting list will iterating on it
    indices.sort().reverse()
    indices.forEach((index) => dispatch('removeColumnByIndex', { index }))
  },
  removeColumnByIndex({ state, commit }, { index }) {
    const isVisible = index > -1 && index < state.visibleColumnIds.length
    if (isVisible) {
      const id = state.visibleColumnIds[index]
      commit('REMOVE_COLUMN', index)
      commit('UNSET_FILTER', id)
    }
  },
  changeOwnership({ commit }, ownership) {
    commit('SET_OWNERSHIP', ownership)
  },
  changeSort({ state, commit }, sort) {
    if (sort.columnId === state.sortId) {
      commit('REVERSER_SORT_ORDER')
    } else {
      commit('SET_SORT_ID', sort.columnId)
      if (sort.order) {
        commit('SET_SORT_ORDER', sort.order)
      } else {
        commit('SET_SORT_ORDER', 'asc')
      }
    }
  },
  changePageSize({ commit }, pageSize) {
    commit('SET_PAGE_SIZE', pageSize)
  },
  changeSearch({ commit }, search) {
    commit('UNSET_ALL_FILTERS')
    commit('SET_SEARCH', search)
  },
  addFilter({ commit }, { id, value }) {
    commit('SET_SEARCH', '')
    commit('SET_FILTER', { id, value })
  },
  removeFilter({ commit }, id) {
    commit('UNSET_FILTER', id)
  },
  clearFilters({ commit }) {
    commit('UNSET_ALL_FILTERS')
  },
  loadSavedFilters({ state, commit }) {
    for (let filterId of Object.keys(state.savedFilters)) {
      commit('SET_FILTER', {
        id: filterId,
        value: state.savedFilters[filterId],
      })
    }
    state.savedFilters = {}
  },
}

const mutations = {
  INIT_SETTINGS(state, settings) {
    Object.assign(state, cloneDeep(settings))
  },
  SET_OWNERSHIP(state, ownership) {
    if (state.ownership?.name !== ownership?.name) state.ownership = ownership
  },
  SET_SORT_ID(state, sortId) {
    state.sortId = sortId
  },
  SET_SORT_ORDER(state, sortOrder) {
    state.sortOrder = sortOrder
  },
  REVERSER_SORT_ORDER(state) {
    state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc'
  },
  SET_PAGE_SIZE(state, pageSize) {
    if (state.pageSize !== pageSize) state.pageSize = pageSize
  },
  SET_SEARCH(state, search) {
    if (state.search !== search) state.search = search
  },
  CLEAR_SEARCH(state) {
    state.search = ''
  },
  SET_SEARCH_VISIBLE_ONLY(state, searchVisibleOnly) {
    state.searchVisibleOnly = searchVisibleOnly
  },
  SET_VISIBLE_COLUMNS_IDS(state, visibleColumnIds) {
    state.visibleColumnIds = visibleColumnIds.filter(
      // the filter uniqifies the list
      (el, i) => visibleColumnIds.indexOf(el) === i,
    )
  },
  MOVE_COLUMN(state, { fromIndices, toIndex }) {
    const ids = fromIndices.reduce((ids, i) => {
      ids.push(state.visibleColumnIds[i])
      return ids
    }, [])
    const filtered = state.visibleColumnIds.filter((vId) => !ids.includes(vId))
    state.visibleColumnIds = [
      ...filtered.slice(0, toIndex),
      ...ids,
      ...filtered.slice(toIndex),
    ]
  },
  REMOVE_COLUMN(state, index) {
    state.visibleColumnIds.splice(index, 1)
  },
  INSERT_COLUMN(state, { id, index }) {
    state.visibleColumnIds.splice(index, 0, id)
  },
  SET_FILTER(state, { id, value }) {
    state.filters[id] = value
  },
  SAVE_FILTERS(state) {
    state.savedFilters = cloneDeep(state.filters)
  },
  UNSET_FILTER(state, id) {
    if (id in state.filters) delete state.filters[id]
  },
  UNSET_ALL_FILTERS(state) {
    for (let filterId of Object.keys(state.filters)) {
      delete state.filters[filterId]
    }
  },
  SET_CONTEXT(state, context) {
    state.context = context
  },
  UNSET_CONTEXT(state) {
    state.context = getDefaultContext()
  },
}

const getters = {
  defaultSortOrder: (state) => {
    return state.modelName.startsWith('order') ||
      state.modelName === 'experiments'
      ? 'desc'
      : 'asc'
  },
  defaultSortId: (state) => {
    return state.modelName.startsWith('order') ||
      state.modelName === 'experiments'
      ? 'created'
      : 'name'
  },
  defaultSettings: (state, getters) => {
    return {
      search: '',
      searchVisibleOnly: false,
      ownership: { name: 'all' },
      pageSize: 10,
      sortOrder: getters.defaultSortOrder,
      sortId: getters.defaultSortId,
    }
  },
  serverSettings: (state, getters, rootState, rootGetters) => {
    return rootGetters[
      ['serverSettings', state.vuexUid, 'nonEmptySettings'].join('/')
    ]
  },
  settingsToSave: (state, getters) => {
    const settings = {}
    const serverSettings = getters.serverSettings
    // need to be "SAVABLE" and "NOT DEFAULT"
    const defaultSettings = getters.defaultSettings
    for (let settingKey of SAVABLE_SETTING) {
      const sessionVal = state[settingKey]
      const serverVal = serverSettings[settingKey]
      // const sessionVal = cloneDeep(state[settingKey])
      const defaultVal = defaultSettings[settingKey]
      const hasSessionVal =
        typeof sessionVal !== 'undefined' && sessionVal !== null
      const hasDefaultVal = typeof defaultVal !== 'undefined'
      const hasServerVal = typeof defaultVal !== 'undefined'

      let isDefault = false
      let isEmpty = true
      let isSaved = false
      switch (settingKey) {
        case 'visibleColumnIds':
          isEmpty = hasSessionVal && sessionVal.length < 1
          isDefault = hasDefaultVal && isEqual(sessionVal, defaultVal)
          isSaved = hasServerVal && isEqual(sessionVal, serverVal)
          break
        case 'filters':
          isEmpty = hasSessionVal && _isEmpty(sessionVal)
          isDefault = hasDefaultVal && isEqual(sessionVal, defaultVal)
          isSaved = isEqual(sessionVal, serverVal)
          break
        case 'pageSize':
          isEmpty = hasSessionVal && sessionVal < 0
          isDefault = hasDefaultVal && sessionVal === defaultVal
          isSaved = hasServerVal && isEqual(sessionVal, serverVal)
          break
        default:
          isEmpty = !hasSessionVal
          isDefault = hasDefaultVal && isEqual(sessionVal, defaultVal)
          isSaved = hasServerVal && isEqual(sessionVal, serverVal)
      }

      if (!isSaved || (!isEmpty && !isDefault)) {
        settings[settingKey] = sessionVal
      }
    }
    return settings
  },

  ownership(state) {
    return state.ownership
  },
  sort(state) {
    return {
      order: state.sortOrder,
      columnId: state.sortId,
    }
  },
  pageSize(state) {
    return state.pageSize
  },
  visibleColumnIds(state) {
    return state.visibleColumnIds
  },
  isVisible(state, id) {
    return state.visibleColumnIds.indexOf(id) > -1
  },
  search(state) {
    return state.search
  },
  searchVisibleOnly(state) {
    return state.searchVisibleOnly
  },
  hasSearch(state) {
    return state.search !== ''
  },
  selected(state) {
    return state.selected
  },
  filters(state) {
    return state.filters
  },
  hasFilters(state) {
    return Object.keys(state.filters).length
  },
  context(state) {
    return state.context
  },
}

const sessionSettings = {
  namespaced: true,
  state: () => getDefaultState(),
  actions,
  getters,
  mutations,
}

export default sessionSettings
